import React, { createContext, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);

  const [confirmData, setConfirmData] = useState(null);

  const showNotification = useCallback((message, type = 'info', duration = 5000, options = {}) => {
    const id = uuidv4();
    // options: { position: 'bottom-right' (default) | 'bottom-left', hideMobile: boolean }
    const position = options.position === 'bottom-left' ? 'bottom-left' : 'bottom-right';
    const hideMobile = !!options.hideMobile;
    setNotifications((prev) => [...prev, { id, message, type, duration, position, hideMobile }]);

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, duration);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmData({
        message,
        title: options.title || t('common.confirmation'),
        confirmText: options.confirmText || t('common.yes'),
        cancelText: options.cancelText || t('common.cancel'),
        resolve
      });
    });
  }, [t]);

  const handleConfirm = useCallback((value) => {
    if (confirmData) {
      confirmData.resolve(value);
      setConfirmData(null);
    }
  }, [confirmData]);

  return (
    <NotificationContext.Provider value={{
      showNotification,
      removeNotification,
      notifications,
      confirm,
      confirmData,
      handleConfirm
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
